import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Drawer,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Stack,
  Grid,
  Link,
  Chip,
  LinearProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DOMPurify from "dompurify";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MovieIcon from "@mui/icons-material/Movie";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PersonIcon from "@mui/icons-material/Person";
import RichTextEditor from "./components/RichTextEditor";
import { formatSmartDate } from "./utils/dateUtils";
import api from "./api/api";
import { useTheme, useMediaQuery } from "@mui/material";
import { ENDPOINTS } from "./api/endpoints";

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "video/mp4",
  "video/quicktime",
  "image/gif",
  "audio/mpeg",
];

const MAX_MESSAGE_LENGTH = 2000;
const MAX_FILE_SIZE = 300 * 1024 * 1024; // 100 MB

// ─── Pure Helpers ─────────────────────────────────────────────────────────────

/**
 * Returns "Director" or "Talent" based on the session stored in localStorage,
 * or null if the role is missing / unrecognised.
 */
const getCurrentUserRole = () => {
  try {
    const session = JSON.parse(localStorage.getItem("loggedInUser"));
    const role = session?.data?.userRole;
    if (!role) return null;
    return role === "COMPANY" ? "Director" : "Talent";
  } catch {
    return null;
  }
};

/**
 * Returns the email of the currently logged-in user from localStorage,
 * or an empty string as fallback.
 */
const getCurrentUserEmail = () => {
  try {
    const session = JSON.parse(localStorage.getItem("loggedInUser"));
    return session?.data?.userEmail || localStorage.getItem("email") || "";
  } catch {
    return localStorage.getItem("email") || "";
  }
};

/**
 * Returns "FirstName LastName" of the logged-in user, or falls back to their
 * email so optimistic replies always show a human-readable sender label.
 */
const getCurrentUserDisplayName = () => {
  try {
    const session = JSON.parse(localStorage.getItem("loggedInUser"));

    const firstName = session?.data?.userFirstName || "";
    const lastName = session?.data?.userLastName || "";

    const fullName = `${firstName} ${lastName}`.trim();

    return (
      fullName || localStorage.getItem("userName") || getCurrentUserEmail()
    );
  } catch {
    return localStorage.getItem("userName") || getCurrentUserEmail();
  }
};

const normalizeLinks = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";

  div.querySelectorAll("a").forEach((a) => {
    let href = a.getAttribute("href");

    if (
      href &&
      !href.startsWith("http://") &&
      !href.startsWith("https://") &&
      !href.startsWith("mailto:")
    ) {
      href = `https://${href}`;
      a.setAttribute("href", href);
    }

    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });

  return div.innerHTML;
};

/**
 * Validates a file against the allowed types and max size.
 * Returns an error string, or "" if the file is valid.
 */
const validateFile = (file) => {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return "Invalid file type. Allowed: PDF, JPG, PNG, MP4, MOV, GIF, MP3.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size exceeds the 100 MB limit.";
  }
  return "";
};

/**
 * Extracts the YouTube video ID from a variety of YouTube URL formats.
 * Returns "" if the URL is not recognised.
 */
const getYoutubeVideoId = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname.slice(1);
    }
    if (parsed.searchParams.get("v")) {
      return parsed.searchParams.get("v");
    }
    if (parsed.pathname.includes("/shorts/")) {
      return parsed.pathname.split("/shorts/")[1];
    }
    return "";
  } catch {
    return "";
  }
};

const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  try {
    // YouTube
    const ytId = getYoutubeVideoId(url);
    if (ytId)
      return {
        type: "youtube",
        embedUrl: `https://www.youtube.com/embed/${ytId}`,
      };

    // Vimeo: vimeo.com/123456789
    const vimeo = new URL(url);
    if (vimeo.hostname.includes("vimeo.com")) {
      const vimeoId = vimeo.pathname.split("/").filter(Boolean).pop();
      if (vimeoId)
        return {
          type: "vimeo",
          embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
        };
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Extracts and truncates a filename from a URL or file path.
 */
const extractFileName = (url) => {
  if (!url) return "Unknown File";
  try {
    const fileName = new URL(url).pathname.split("/").pop();
    return fileName.length > 20 ? `${fileName.substring(0, 20)}…` : fileName;
  } catch {
    const raw = url.substring(url.lastIndexOf("/") + 1);
    return raw.length > 20 ? `${raw.substring(0, 20)}…` : raw;
  }
};

/**
 * Normalises raw reply objects returned from the API so that the rest of
 * the component can rely on a consistent shape.
 */
const formatReplies = (replies) =>
  (replies || []).map((reply) => ({
    ...reply,
    File: reply.File || null,
    fileURL: reply.fileURL || null,
    conversationDate: reply.conversationDate || "",
  }));

/**
 * Fetches a remote URL, converts it to a Blob, and returns a File object.
 * Used to re-attach thread attachments that only exist as remote URLs.
 */
const remoteUrlToFile = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const name = url.split("/").pop() || "attachment";
  return new File([blob], name, { type: blob.type });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Renders a single reply bubble.
 *
 * - isOwnMessage  → right-aligned, light-blue background  (the current user)
 * - !isOwnMessage → left-aligned,  light-gray background  (the other person)
 *
 * displayName is passed explicitly so optimistic replies (which only have
 * the raw email in fromEmail) still show a readable label rather than an
 * email address.
 */
const ReplyMessage = ({ reply, firstSenderEmail }) => {
  const isOwnMessage = (() => {
    const role = getCurrentUserRole(); // "Director" or "Talent"
    const receipt = reply.messageReceipt?.trim().toLowerCase();

    if (role === "Director") {
      // Director's own messages are sent to talent (an email), not "CD"
      return receipt !== "cd";
    }

    if (role === "Talent") {
      // Talent's own messages are sent to "CD"
      return receipt === "cd";
    }

    return false;
  })();

  const senderLabel = reply.fromEmail?.trim() || reply.userName || "Unknown";

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid #ddd",
        borderRadius: 2,
        backgroundColor: isOwnMessage ? "#e3f2fd" : "#f5f5f5",
        alignSelf: isOwnMessage ? "flex-end" : "flex-start",
        maxWidth: "75%",
      }}
    >
      <Typography
        fontWeight={600}
        sx={{ fontSize: 13, color: "gray", mb: 0.5 }}
      >
        {senderLabel}
      </Typography>
      <Typography
        sx={{
          wordBreak: "break-word",
          overflowWrap: "anywhere",

          "& a": {
            color: "#1976d2",
            textDecoration: "underline",
            cursor: "pointer",
          },

          "& a:hover": {
            textDecoration: "underline",
          },

          "& ul": {
            listStyleType: "disc",
            paddingLeft: "24px",
            margin: "8px 0",
          },

          "& ol": {
            listStyleType: "decimal",
            paddingLeft: "24px",
            margin: "8px 0",
          },

          "& li": {
            display: "list-item",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: normalizeLinks(
            DOMPurify.sanitize(
              reply.msgText ? reply.msgText.replace(/<\/?pre>/g, "") : "",
              {
                ADD_ATTR: ["target", "rel"],
              },
            ),
          ),
        }}
      />

      {/* YouTube embed */}
      {/*       {reply.youtubeURL && (
        <Box sx={{ mt: 1 }}>
          <iframe
            width="100%"
            height="200"
            src={`https://www.youtube.com/embed/${getYoutubeVideoId(reply.youtubeURL)}`}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      )} */}

      {/* File attachments */}
      {/* File attachments / Video embeds */}
      {[reply.fileURL, reply.File].filter(Boolean).map((file, idx) => {
        const videoEmbed = getVideoEmbedUrl(file);

        //  Render as embedded video player if YouTube or Vimeo
        if (videoEmbed) {
          return (
            <Box key={idx} sx={{ mt: 1, borderRadius: 2, overflow: "hidden" }}>
              <iframe
                width="100%"
                height="200"
                src={videoEmbed.embedUrl}
                title={`${videoEmbed.type} video`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          );
        }

        //  Render as file attachment for everything else (PDF, image, etc.)
        return (
          <Card
            key={idx}
            variant="outlined"
            sx={{
              mt: 1,
              p: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: 2,
              background: "#fafafa",
            }}
          >
            <InsertDriveFileIcon color="action" />
            <Link
              href={file}
              target="_blank"
              underline="hover"
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {extractFileName(file)}
            </Link>
          </Card>
        );
      })}

      <Typography sx={{ fontSize: 11, color: "#64b5f6", mt: 1 }}>
        {formatSmartDate(reply.conversationDate)}
      </Typography>
    </Box>
  );
};

/**
 * YouTube URL input row shown when the user clicks the YouTube icon.
 */
const YouTubeInput = ({ show, link, onLinkChange, onClear, disabled }) => {
  if (!show) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <YouTubeIcon sx={{ color: "red" }} fontSize="small" />
        <TextField
          fullWidth
          size="small"
          placeholder="Paste YouTube URL…"
          value={link}
          onChange={(e) => onLinkChange(e.target.value)}
          disabled={disabled}
        />
        {link && (
          <IconButton size="small" onClick={onClear} disabled={disabled}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {/* Live preview once a valid ID is detected */}
      {getYoutubeVideoId(link) && (
        <Box sx={{ mt: 1, borderRadius: 2, overflow: "hidden" }}>
          <iframe
            width="100%"
            height="150"
            src={`https://www.youtube.com/embed/${getYoutubeVideoId(link)}`}
            title="YouTube preview"
            frameBorder="0"
            allowFullScreen
          />
        </Box>
      )}
    </Box>
  );
};

/**
 * Attachment grid shown in the right-hand details panel.
 */
const AttachmentGrid = ({ allAttachments }) => {
  if (!allAttachments.length) return null;

  return (
    <>
      <Typography variant="h6" sx={{ fontSize: { xs: 16, md: 18 }, mb: 1 }}>
        Attachments
      </Typography>

      <Stack spacing={2}>
        <Grid container spacing={1}>
          {allAttachments.map((att, idx) => {
            const ext = att.split(".").pop().toLowerCase();
            let Icon = InsertDriveFileIcon;
            if (["jpg", "jpeg", "png", "gif"].includes(ext)) Icon = ImageIcon;
            else if (ext === "pdf") Icon = PictureAsPdfIcon;
            else if (["mp4", "mov"].includes(ext)) Icon = MovieIcon;
            else if (["mp3", "wav"].includes(ext)) Icon = AudiotrackIcon;

            return (
              <Grid item xs={12} sm={6} key={idx}>
                <Card
                  variant="outlined"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <IconButton size="small">
                    <Icon fontSize="small" />
                  </IconButton>
                  <Link
                    href={att}
                    target="_blank"
                    underline="none"
                    sx={{
                      flexGrow: 1,
                      ml: 1,
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {extractFileName(att)}
                  </Link>
                  {["jpg", "jpeg", "png", "gif"].includes(ext) && (
                    <Chip
                      size="small"
                      label="Preview"
                      onClick={() => window.open(att, "_blank")}
                      sx={{ mr: 1 }}
                    />
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ReplyDrawer = ({ open, onClose, selectedMessage }) => {
  // ── Refs ──────────────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);
  // Keeps track of object URLs created for optimistic previews so we can
  // revoke them and avoid memory leaks (FIX #2).
  const objectUrlsRef = useRef([]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [replyText, setReplyText] = useState("");
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [showYouTubeInput, setShowYouTubeInput] = useState(false);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [canReply, setCanReply] = useState(true);
  const user =
  localStorage.getItem("userRole") === "USER"
    ? "Talent"
    : localStorage.getItem("userRole") === "COMPANY"
    ? "Director"
    : "";

  // ── Responsive helpers ────────────────────────────────────────────────────
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [composerOpen, setComposerOpen] = useState(!isMobile);

  // ── Derived / stable values ───────────────────────────────────────────────
  // Read identity from localStorage once — stable across renders.
  const currentUserEmail = useMemo(() => getCurrentUserEmail(), []);
  const userRole = useMemo(() => getCurrentUserRole(), []);

  const [threadId, setThreadId] = useState(
  Number(selectedMessage?.originalMessageId) || 0
);
  const [conversationParticipants, setConversationParticipants] = useState({
    directorEmail: null,
    talentEmail: null,
  });

  const messagesContainerRef = useRef(null);
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [replies]);

  /**
   * Collect every unique attachment URL across all replies so we can display
   * them in the details panel and forward them on new sends.
   */
  const allAttachments = useMemo(
    () =>
      replies.flatMap((reply) => [reply.fileURL, reply.File].filter(Boolean)),
    [replies],
  );

  /** The avatar to display is always the *other* person's image. */
  const avatarUrl = useMemo(() => {
    if (userRole === "Director") return selectedMessage?.talentImage || null;
    if (userRole === "Talent") return selectedMessage?.directorImage || null;
    return null;
  }, [userRole, selectedMessage]);

  /** Friendly display name for the conversation partner. */
  const conversationName = useMemo(() => {
    if (!selectedMessage) return "";
    return currentUserEmail === selectedMessage.fromEmail
      ? selectedMessage.recipientName || selectedMessage.messageReceipt
      : selectedMessage.talentName || selectedMessage.fromEmail;
  }, [currentUserEmail, selectedMessage]);

  /** Date of the most recent message. */
  const lastMessageDate = useMemo(() => {
    if (!replies.length) return "No messages yet";
    return formatSmartDate(replies[replies.length - 1].conversationDate);
  }, [replies]);

  // ── Side effects ──────────────────────────────────────────────────────────

  // Reset composer state whenever the selected conversation changes.
  useEffect(() => {
    setComposerOpen(!isMobile);
  }, [selectedMessage, isMobile]);

  useEffect(() => {
  setThreadId(Number(selectedMessage?.originalMessageId) || 0);
}, [selectedMessage]);

  // Reset avatar error state whenever the avatar URL changes.
  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  /**
   * Revoke any object URLs we created when the component unmounts, or when
   * a new message is selected, to prevent memory leaks (FIX #2).
   */
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    };
  }, [selectedMessage]);

  // Fetches the reply thread. `silent: true` is used by the poller so it
  // doesn't toggle the loading spinner or wipe replies on a failed request.
const fetchReplies = useCallback(
  async ({ silent = false } = {}) => {
    if (!threadId) {
      setReplies([]);
      setCanReply(true);
      setConversationParticipants({
        directorEmail: null,
        talentEmail: null,
      });
      return;
    }

    if (!silent) {
      setLoadingReplies(true);
    }

    try {
      const response = await api.get(ENDPOINTS.showInboxMessages(), {
        params: {
          originalMessageId: threadId,
          status: "r",
          user: user,
        },
      });

      const data = response.data?.data || [];

      setReplies(formatReplies(data));

      setCanReply((data[0]?.canReply ?? data[0]?.CanReply) !== false);

      const firstMessage = data[0];

      if (firstMessage?.senderEmail && firstMessage?.recipientEmail) {
        setConversationParticipants({
          directorEmail: firstMessage.senderEmail,
          talentEmail: firstMessage.recipientEmail,
        });
      }
    } catch (error) {
      console.error("Error fetching replies:", error);

      if (!silent) {
        setReplies([]);
      }
    } finally {
      if (!silent) {
        setLoadingReplies(false);
      }
    }
  },
  [threadId]
);

  // Initial load whenever the selected conversation changes.
useEffect(() => {
  fetchReplies();
}, [fetchReplies]);

  // Poll for new replies every 15s while the drawer is open.
useEffect(() => {
  if (!open || !threadId) return;

  const intervalId = setInterval(() => {
    fetchReplies({ silent: true });
  }, 15000);

  return () => clearInterval(intervalId);
}, [open, threadId, fetchReplies]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Determines who the reply should be sent *to*.
   *
   * The first message in any thread is always Director -> Talent, so
   * replies[0].senderEmail / recipientEmail give us a reliable, fixed pair
   * of participant emails for the whole conversation. We just pick whichever
   * one isn't the current user.
   */
  const determineRecipientEmail = useCallback(() => {
    const { directorEmail, talentEmail } = conversationParticipants;

    if (directorEmail && talentEmail) {
      return userRole === "Director" ? talentEmail : directorEmail;
    }

    // Fallback for the (rare) case replies haven't loaded yet.
    const from = selectedMessage?.fromEmail;
    const receipt = selectedMessage?.messageReceipt;
    const fallback = selectedMessage?.talentEmail;
    let to = currentUserEmail === from ? receipt || fallback : from;
    if (!to || !to.includes("@")) to = fallback || from;
    return to;
  }, [conversationParticipants, userRole, currentUserEmail, selectedMessage]);

  /**
   * Builds the FormData payload for a message send.
   *
   * FIX #4: Thread attachments that are remote URLs are now awaited before
   * the FormData is returned, so they are always included in the request.
   *
   * @param {string}  messageText     - HTML message body.
   * @param {object}  [additionalData] - Optional extra fields (subject, youtubeURL, isYouTube).
   * @returns {Promise<FormData>}
   */
  const buildFormData = useCallback(
    async (messageText, additionalData = {}) => {
      const role = getCurrentUserRole();
      if (!role) throw new Error("User role not available");

      const talentId = selectedMessage?.talentId || selectedMessage?.userId;
      if (!talentId) throw new Error("TalentId is missing");

      const formData = new FormData();
      const toEmail = determineRecipientEmail();

      formData.append("MessageText", messageText); //  text only
      formData.append("FromEmail", currentUserEmail);
      formData.append("NoticeId", selectedMessage?.noticeId || 0);

      if (selectedMessage?.noticeTitle) {
        formData.append("NoticeTitle", selectedMessage.noticeTitle);
      }

      formData.append("ToEmail", toEmail);
      formData.append("RecipientName", selectedMessage?.talentName || "");
      formData.append("TalentId", talentId);
      formData.append("CanReply", "true");
     formData.append(
  "MessageSubject",
  selectedMessage?.noticeTitle?.trim()
    ? selectedMessage.noticeTitle
    : (selectedMessage?.messageSubject || "Direct message")
);
formData.append("ParentId", threadId || 0);
      formData.append("UserEmail", currentUserEmail);
      formData.append("MessageFrom", role);
      formData.append("MessageFromName",  getCurrentUserDisplayName());

      //  Binary file upload → File field
      if (selectedFile) {
        formData.append("File", selectedFile);
      }

      //  External URL (YouTube/Vimeo/etc) → FileURL field
      if (additionalData.fileURL) {
        formData.append("FileURL", additionalData.fileURL);
      }

      // Thread attachments (existing remote URLs)
      const remoteAttachments = allAttachments.filter(
        (att) => typeof att === "string",
      );
      await Promise.all(
        remoteAttachments.map(async (att, idx) => {
          try {
            const file = await remoteUrlToFile(att);
            formData.append(`ThreadAttachment${idx}`, file);
          } catch (err) {
            console.error("Failed to fetch thread attachment:", att, err);
          }
        }),
      );

      allAttachments
        .filter((att) => att instanceof File)
        .forEach((att, idx) => {
          formData.append(`ThreadAttachmentLocal${idx}`, att);
        });

      return formData;
    },
    [
      currentUserEmail,
      determineRecipientEmail,
      selectedMessage,
      selectedFile,
      allAttachments,
      threadId
    ],
  );

  /**
   * Posts a FormData payload to the send-message endpoint and appends an
   * optimistic reply to the local list.
   */
const dispatchMessage = useCallback(async (formData) => {
  setUploadProgress(0);

  const response = await api.post(ENDPOINTS.sendMessage(), formData, {
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percent);
      }
    },
  });

  const newThreadId = response.data?.data?.originalMessageId;

  if (newThreadId && !threadId) {
    setThreadId(newThreadId);
  }

  return response.data;
}, [threadId]);

  // ── Event handlers ────────────────────────────────────────────────────────

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
    } else {
      setFileError("");
      setSelectedFile(file);
      //  Clear YouTube if file is selected
      setYoutubeLink("");
      setShowYouTubeInput(false);
    }

    event.target.value = "";
  }, []);

  const handleFileRemove = useCallback(() => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!canReply) return;

    const plainText = replyText.replace(/<(.|\n)*?>/g, "").trim();
    const hasYoutube = showYouTubeInput && youtubeLink.trim();

    // Must have at least text OR a YouTube link
    if (!plainText && !hasYoutube) return;

    setSending(true);
    try {
      const messageText = plainText ? replyText : "";

      const formData = await buildFormData(messageText, {
        ...(hasYoutube && {
          fileURL: youtubeLink,
          subject: "YouTube Video Reply",
        }),
      });

    await dispatchMessage(formData);
await fetchReplies({ silent: true });
      // Reset everything
      setSelectedFile(null);
      setReplyText("");
      setFileError("");
      setYoutubeLink("");
      setShowYouTubeInput(false);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  }, [
    replyText,
    buildFormData,
    currentUserEmail,
    selectedFile,
    dispatchMessage,
    youtubeLink,
    showYouTubeInput,
    userRole,
    canReply,
  ]);

  const handleAddYouTubeVideo = useCallback(async () => {
    if (!youtubeLink.trim()) return;

    setSending(true);
    try {
      const formData = await buildFormData(
        `<a href="${youtubeLink}" target="_blank">${youtubeLink}</a>`,
        {
          subject: "YouTube Video Reply",
          youtubeURL: youtubeLink,
          isYouTube: true,
        },
      );
     await dispatchMessage(formData);
await fetchReplies({ silent: true });

      setYoutubeLink("");
      setShowYouTubeInput(false);
      setYoutubeLink("");
      setShowYouTubeInput(false);
    } catch (error) {
      console.error("Error sending YouTube video:", error);
    } finally {
      setSending(false);
    }
  }, [youtubeLink, buildFormData, currentUserEmail, dispatchMessage]);

  // ── Early return ──────────────────────────────────────────────────────────

  if (!open || !selectedMessage) return null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        disableScrollLock: false,
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "85%", md: 1000 },
          height: "100vh",
          overflow: "hidden",
          overscrollBehavior: "contain",
        },
      }}
    >
      {/* Close button */}
      <Box sx={{ m: 2 }}>
        <IconButton onClick={onClose} aria-label="Close conversation">
          <CloseIcon />
        </IconButton>
      </Box>

      <Container
        disableGutters
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
          p: 2,
        }}
      >
        {/* ── Left panel: messages + composer ── */}
        <Box
          sx={{
            flex: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            height: "100%",
            overscrollBehavior: "contain",
            borderRight: { md: "1px solid #ddd" },
            pr: { md: 2 },
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="h6">
  {selectedMessage?.messageSubject?.trim()
    ? selectedMessage?.noticeTitle?.trim()
      ? `${selectedMessage.messageSubject}`
      : selectedMessage.messageSubject
    : selectedMessage?.noticeTitle}
</Typography>
              <Typography sx={{ fontSize: 12, color: "gray" }}>
                {lastMessageDate}
              </Typography>
            </Box>

            {isMobile && (
              <Button size="small" onClick={() => setDetailsOpen(true)}>
                View Details
              </Button>
            )}
          </Box>

          {/* Message list */}
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {loadingReplies ? (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress />
              </Box>
            ) : replies.length === 0 ? (
              <Typography sx={{ color: "gray", mt: 4, textAlign: "center" }}>
                No messages yet. Start the conversation!
              </Typography>
            ) : (
              replies.map((reply, index) => (
                <ReplyMessage
                  key={reply.id || index}
                  reply={reply}
                  firstSenderEmail={replies?.[0]?.fromEmail}
                />
              ))
            )}
          </Box>

          {/* Mobile: tap-to-open composer strip */}
          {isMobile && !composerOpen && (
            <Box
              onClick={() => setComposerOpen(true)}
              sx={{
                position: "sticky",
                bottom: 0,
                p: 1.5,
                fontSize: 14,
                background: "#f9f9f9",
                borderTop: "1px solid #eee",
                cursor: "pointer",
                color: "gray",
              }}
            >
              Tap to reply…
            </Box>
          )}

          {/* Reply composer */}
          {composerOpen && canReply && (
            <Box
              sx={{
                flexShrink: 0,
                background: "#fff",
                p: 2,
                borderTop: "1px solid #eee",
                maxHeight: "50vh",
                overflowY: "auto",
              }}
            >
              {/* Mobile cancel */}
              {isMobile && (
                <Box textAlign="right" sx={{ marginBottom: "8px" }}>
                  <Button size="small" onClick={() => setComposerOpen(false)}>
                    Cancel
                  </Button>
                </Box>
              )}

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.gif,.mp3,.mkv"
                onChange={handleFileUpload}
              />

              <RichTextEditor
                value={replyText}
                onChange={setReplyText}
                maxLength={MAX_MESSAGE_LENGTH}
                toolbarHidden={isMobile}
                editorStyle={{
                  minHeight: isMobile ? 120 : 160,
                }}
              />

              {/* Upload progress — shown in the composer where it belongs (FIX #1) */}
              {sending && uploadProgress > 0 && uploadProgress < 100 && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: 12, mb: 0.5 }}>
                    Uploading… {uploadProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                </Box>
              )}

              {/* Action row */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}
              >
                <IconButton
                  disabled={sending || showYouTubeInput}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Attach file"
                  sx={{
                    opacity: showYouTubeInput ? 0.4 : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  <AttachFileIcon />
                </IconButton>

                <IconButton
                  disabled={sending || !!selectedFile}
                  onClick={() => {
                    if (!showYouTubeInput) {
                      setSelectedFile(null);
                      setFileError("");
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }
                    setShowYouTubeInput((v) => !v);
                  }}
                  aria-label="Attach YouTube video"
                  sx={{
                    opacity: selectedFile ? 0.4 : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  <YouTubeIcon />
                </IconButton>

                <Box sx={{ flex: 1 }} />

                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  disabled={
                    sending ||
                    ((!replyText ||
                      replyText.replace(/<(.|\n)*?>/g, "").trim() === "") &&
                      !(showYouTubeInput && youtubeLink.trim()))
                  }
                  onClick={async () => {
                    await handleSendMessage();
                    if (isMobile) setComposerOpen(false);
                  }}
                  sx={{ borderRadius: 5, px: 3 }}
                >
                  {sending ? "Sending…" : "Send"}
                </Button>
              </Box>

              {/* YouTube URL input */}
              <YouTubeInput
                show={showYouTubeInput}
                link={youtubeLink}
                onLinkChange={setYoutubeLink}
                onClear={() => {
                  setYoutubeLink("");
                  setShowYouTubeInput(false);
                }}
                disabled={sending}
              />

              {/* Selected file preview */}
              {selectedFile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1.5,
                    p: 1,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    width: "fit-content",
                    background: "#fafafa",
                  }}
                >
                  <InsertDriveFileIcon fontSize="small" />
                  <Typography sx={{ fontSize: 13 }}>
                    {selectedFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleFileRemove}
                    aria-label="Remove file"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* File error */}
              {fileError && (
                <Typography sx={{ color: "error.main", fontSize: 12, mt: 0.5 }}>
                  {fileError}
                </Typography>
              )}
            </Box>
          )}
          {!canReply && (
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #eee",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography color="text.secondary">
                Replies are disabled for this conversation. Please contact the
                sender directly.
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Right panel: avatar + attachments (desktop only) ── */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            pl: 2,
          }}
        >
          {/* Avatar card */}
          <Box sx={{ mb: 2 }}>
  <Box
    sx={{
      width: "100%",
      aspectRatio: "3 / 4",
      overflow: "hidden",
      borderRadius: 1,
    }}
  >
    <Avatar
      src={!imgError && avatarUrl ? avatarUrl : undefined}
      onError={() => setImgError(true)}
      variant="rounded"
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      {imgError && <PersonIcon fontSize="large" />}
    </Avatar>
  </Box>

  <CardContent
    sx={{
      textAlign: "center",
      pt: 1.5,
      backgroundColor: "#202020",
      color: "#fff",
    }}
  >
    <Typography variant="h6" sx={{ fontSize: "1rem" }}>
      {selectedMessage?.talentName}
    </Typography>
  </CardContent>
</Box>

          {/* Attachments grid */}
          <AttachmentGrid allAttachments={allAttachments} />
        </Box>
      </Container>

      {/* ── Mobile bottom sheet: details ── */}
      <Drawer
        anchor="bottom"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        PaperProps={{
          sx: {
            height: "70%",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2,
          },
        }}
      >
        <Box textAlign="right">
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </Box>

        {/* Avatar */}
        <Box textAlign="center" mb={2}>
          <Avatar
            src={!imgError && avatarUrl ? avatarUrl : undefined}
            sx={{ width: 100, height: 100, margin: "auto" }}
          >
            {imgError && <PersonIcon />}
          </Avatar>
          <Typography mt={1} fontWeight={600}>
            {selectedMessage?.talentName}
          </Typography>
        </Box>

        {/* Attachments list */}
        {allAttachments.length > 0 && (
          <>
            <Typography variant="h6">Attachments</Typography>
            <Stack spacing={1} mt={1}>
              {allAttachments.map((att, idx) => (
                <Card key={idx} sx={{ p: 1 }}>
                  <Link href={att} target="_blank" underline="hover">
                    {extractFileName(att)}
                  </Link>
                </Card>
              ))}
            </Stack>
          </>
        )}
      </Drawer>
    </Drawer>
  );
};

export default ReplyDrawer;
